import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
	Alert,
	AlertIcon,
	AlertTitle,
	AlertDescription,
	Button,
	Box,
	Flex,
	useDisclosure,
} from '@chakra-ui/react';
import { FaArrowUp, FaEnvelope } from 'react-icons/fa';
import { useAuthenticatedUserInfo } from '../../services/Contexts/AuthenticatedUserInfoContext';
import { useEmailUsage } from '../../hooks/useSendGridSubuser';
import { useSyncEmailPackLimits } from '../../hooks/useEmailPacks';
import EmailPackPurchaseModal from '../EmailPacks/EmailPackPurchaseModal';

/**
 * Global banner when monthly email send quota is exhausted.
 * Offers add-on / top-up purchase in addition to plan upgrade.
 */
const CampaignEmailSentLimitBanner = () => {
	const { companyId } = useAuthenticatedUserInfo();
	const { data: emailUsage, isLoading: usageLoading, refetch } = useEmailUsage(companyId);
	const { mutate: syncPackLimits } = useSyncEmailPackLimits(companyId);
	const { isOpen, onOpen, onClose } = useDisclosure();

	const emailsSent = emailUsage?.stats?.emailsSentThisMonth ?? 0;
	const effectiveLimit = emailUsage?.stats?.monthlyEmailLimit ?? null;
	const enforcementActive = emailUsage?.stats?.subscriptionInfo?.enforcementActive !== false;

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.get('emailPack') !== 'success' || !companyId) return;

		syncPackLimits(undefined, {
			onSuccess: () => refetch(),
		});

		params.delete('emailPack');
		params.delete('session_id');
		const query = params.toString();
		window.history.replaceState(
			{},
			'',
			`${window.location.pathname}${query ? `?${query}` : ''}`,
		);
	}, [companyId, refetch, syncPackLimits]);

	if (usageLoading) return null;
	if (!enforcementActive) return null;
	if (effectiveLimit === null || effectiveLimit === undefined) return null;
	if (emailsSent < effectiveLimit) return null;

	const planIncluded = emailUsage?.stats?.planIncludedLimit ?? 0;
	const addonLimit = emailUsage?.stats?.emailAddonMonthlyLimit ?? 0;
	const topUpRemaining = emailUsage?.stats?.emailTopUpRemaining ?? 0;

	return (
		<>
			<Alert
				status='error'
				variant='left-accent'
				borderRadius='lg'
				alignItems='center'
				boxShadow='sm'
				mb={3}
				py={3}>
				<AlertIcon />
				<Flex
					flex={1}
					align={{ base: 'stretch', md: 'center' }}
					justify='space-between'
					gap={4}
					flexDirection={{ base: 'column', md: 'row' }}>
					<Box>
						<AlertTitle mb={1}>Monthly email send limit reached</AlertTitle>
						<AlertDescription>
							You have sent {emailsSent.toLocaleString()} of{' '}
							{effectiveLimit.toLocaleString()} campaign emails allowed this month
							{planIncluded > 0 && (
								<>
									{' '}
									(plan {planIncluded.toLocaleString()}
									{addonLimit > 0 ? ` + add-ons ${addonLimit.toLocaleString()}` : ''}
									{topUpRemaining > 0 ? ` + top-ups ${topUpRemaining.toLocaleString()}` : ''})
								</>
							)}
							. Buy a send pack or upgrade your plan to continue.
						</AlertDescription>
					</Box>
					<Flex
						gap={2}
						flexShrink={0}
						flexWrap='wrap'
						alignSelf={{ base: 'flex-start', md: 'center' }}>
						<Button
							colorScheme='orange'
							size='sm'
							leftIcon={<FaEnvelope />}
							onClick={onOpen}>
							Buy more emails
						</Button>
						<Button
							as={RouterLink}
							to='/manage-subscription'
							colorScheme='red'
							variant='outline'
							size='sm'
							leftIcon={<FaArrowUp />}>
							Upgrade plan
						</Button>
					</Flex>
				</Flex>
			</Alert>

			<EmailPackPurchaseModal
				isOpen={isOpen}
				onClose={onClose}
				onPurchaseSuccess={() => {
					syncPackLimits(undefined, { onSuccess: () => refetch() });
				}}
			/>
		</>
	);
};

export default CampaignEmailSentLimitBanner;
